import React, { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Button,
  Box,
  useMantineTheme,
  useMantineColorScheme,
  Group,
} from "@mantine/core";
import { InfoIcon } from "@phosphor-icons/react";
import { DocumentModal } from "@/components/documents/document-modal";
import { EULAContent } from "@/components/documents/eula-content";
import { PrivacyPolicyContent } from "@/components/documents/privacy-policy-content";
import { TermsAndConditionsContent } from "@/components/documents/terms-and-conditions";

interface TermsConditionsModalProps {
  opened: boolean;
  onClose: () => void;
  onAccept: () => void;
  loading?: boolean;
}

const TermsConditionsModal: React.FC<TermsConditionsModalProps> = ({
  opened,
  onClose: _onClose,
  onAccept,
  loading = false,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [isMobile, setIsMobile] = useState(false);
  const [openEulaModal, setOpenEulaModal] = useState<boolean>(false);
  const [openPrivacyModal, setOpenPrivacyModal] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [eulaAccepted, setEulaAccepted] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (opened) {
      // Reset accepted states when modal opens
      setTermsAccepted(false);
      setPrivacyAccepted(false);
      setEulaAccepted(false);
    }
  }, [opened]);

  return (
    <>
      {/* Backdrop */}
      <Box
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 1000,
          display: opened ? "block" : "none",
        }}
      />

      {/* Modal */}
      <Box
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1001,
          display: opened ? "block" : "none",
        }}
      >
        <Box
          style={{
            backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "white",
            borderRadius: "8px",
            padding: isMobile ? "16px" : "32px",
            boxShadow: "0px 1px 4px 0px rgba(12, 12, 13, 0.05), 0px 1px 4px 0px rgba(12, 12, 13, 0.1)",
            minWidth: isMobile ? "320px" : "600px",
            maxWidth: "90vw",
            width: isMobile ? "90vw" : "600px",
            maxHeight: isMobile ? "85vh" : "90vh",
            overflowY: "auto",
          }}
        >
          <Stack gap={isMobile ? "16px" : "24px"} align="center">
            {/* Title */}
            <Text
              fw={510}
              size={isMobile ? "18px" : "20px"}
              c={colorScheme === "dark" ? "white" : "#1D1D1D"}
              ta="center"
              style={{ lineHeight: "1.2em" }}
            >
              Review Our Policies
            </Text>

            <Box
              style={{
                width: "100%",
                maxWidth: isMobile ? "100%" : "741px",
                backgroundColor: colorScheme === "dark" ? theme.colors.dark[5] : "#F5F5F5",
                borderRadius: "8px",
                padding: isMobile ? "12px" : "16px",
              }}
            >
              <Group gap="8px" align="flex-start">
                <InfoIcon 
                  size={isMobile ? 18 : 20} 
                  color={colorScheme === "dark" ? theme.colors.gray[4] : "#393939"}
                  style={{ flexShrink: 0, marginTop: "2px" }}
                />
                <Text
                  size={isMobile ? "13px" : "14px"}
                  c={colorScheme === "dark" ? theme.colors.gray[4] : "#393939"}
                  style={{ 
                    lineHeight: isMobile ? "1.3em" : "1.4em",
                    flex: 1,
                  }}
                >
                  Viewing and agreeing to the Terms & Conditions, Privacy Policy, and End User License Agreement is mandatory before proceeding. Please ensure you have reviewed all documents to continue using OneSelf Path.
                </Text>
              </Group>
            </Box>

            {/* Content */}
            <Stack gap="4px" align="center" style={{ 
              maxWidth: isMobile ? "100%" : "741px",
            }}>
              <Text
                size={isMobile ? "13px" : "14px"}
                c={colorScheme === "dark" ? theme.colors.gray[4] : "#393939"}
                ta="center"
                style={{ 
                  lineHeight: isMobile ? "1.3em" : "1.4em",
                }}
              >
                Before you continue, we kindly ask you to review our{" "}
                <Text
                  component="span"
                  size={isMobile ? "13px" : "14px"}
                  fw={510}
                  c={colorScheme === "dark" ? "white" : "#124B8C"}
                  style={{ 
                    lineHeight: isMobile ? "1.3em" : "1.4em", 
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenTermsModal(true);
                  }}
                >
                  [Terms and Condition]
                </Text>
                ,{" "}
                <Text
                  component="span"
                  size={isMobile ? "13px" : "14px"}
                  fw={510}
                  c={colorScheme === "dark" ? "white" : "#124B8C"}
                  style={{ 
                    lineHeight: isMobile ? "1.3em" : "1.4em", 
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenPrivacyModal(true);
                  }}
                >
                  [Privacy policy]
                </Text>
                , and{" "}
                <Text
                  component="span"
                  size={isMobile ? "13px" : "14px"}
                  fw={510}
                  c={colorScheme === "dark" ? "white" : "#124B8C"}
                  style={{ 
                    lineHeight: isMobile ? "1.3em" : "1.4em", 
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenEulaModal(true);
                  }}
                >
                  [End User License Agreement]
                </Text>
                {" "}to understand your rights, how we handle your data, and the rules for using our platform. By proceeding, you acknowledge that you have read and agreed to these policies.
              </Text>
            </Stack>

            {/* Continue Button */}
            <Button
              onClick={onAccept}
              loading={loading}
              disabled={!termsAccepted || !privacyAccepted || !eulaAccepted}
              size="md"
              style={{
                backgroundColor: (termsAccepted && privacyAccepted && eulaAccepted) ? "#124B8C" : "#CCCCCC",
                color: "white",
                fontWeight: 510,
                fontSize: isMobile ? "13px" : "14px",
                lineHeight: "1.25em",
                padding: isMobile ? "8px 20px" : "10px 16px",
                height: isMobile ? "36px" : "41px",
                borderRadius: "8px",
                border: "none",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                minWidth: isMobile ? "100px" : "120px",
                width: isMobile ? "100%" : "auto",
                cursor: (termsAccepted && privacyAccepted && eulaAccepted) ? "pointer" : "not-allowed",
              }}
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </Box>

      <DocumentModal
        opened={openEulaModal}
        onClose={() => setOpenEulaModal(false)}
        title="End-User License Agreement"
        content={<EULAContent />}
        onAgree={() => {
          setEulaAccepted(true);
          setOpenEulaModal(false);
        }}
        zIndex={2000}
      />

      <DocumentModal
        opened={openPrivacyModal}
        onClose={() => setOpenPrivacyModal(false)}
        title="Privacy Policy"
        content={<PrivacyPolicyContent />}
        onAgree={() => {
          setPrivacyAccepted(true);
          setOpenPrivacyModal(false);
        }}
        zIndex={2000}
      />

      <DocumentModal
        opened={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        title="Terms and Conditions"
        content={<TermsAndConditionsContent />}
        onAgree={() => {
          setTermsAccepted(true);
          setOpenTermsModal(false);
        }}
        zIndex={2000}
      />
    </>
  );
};

export default TermsConditionsModal;
